import { ChargingSchedule, EebusChargingStation } from 'src/Libs/flow/installation/forms'
import { getDefaultEebusEfficientPvSchedule, getDefaultEebusSafePvSchedule } from 'src/Libs/flow/constants/default-devices'
import { ControlledDto, ControlledPOSTDto } from 'src/Libs/flow/installation/api'
import { getLocalStorageValue } from 'src/Libs/flow/hooks'
import { parseControlledDevice } from '../device-parsers'
import { getEvsePairingId } from '../get-device-pairing-id'
import { getEvseEfficientPvScheduleLocalStorageKey, getEvseSafePvScheduleLocalStorageKey } from './local-storage-keys'
import { getSunMode, parseChargingModeSettings } from './sun-mode'
import { ControlledEebusPUTDto } from 'src/Libs/flow/end-user-settings'
import { sanitizeToLatin } from 'src/Libs/flow/installation/utils/sanitize-to-latin'

/** DTO to form Eebus */
export const parseEebusChargingStation = (evse: Nullable<ControlledDto>): EebusChargingStation => {
  const parsedEvse = parseControlledDevice(evse)

  const { chargingMode, chargingWeeklySchedule } = parseChargingModeSettings(evse.parameters?.sunMode ?? null)

  const safePvSchedule: ChargingSchedule = getChargingSchedule(
    chargingMode === 'pvImmediate',
    chargingWeeklySchedule,
    getEvseSafePvScheduleLocalStorageKey(parsedEvse.deviceId),
    getDefaultEebusSafePvSchedule()
  )

  const efficientPvSchedule: ChargingSchedule = getChargingSchedule(
    chargingMode === 'pvDelayed',
    chargingWeeklySchedule,
    getEvseEfficientPvScheduleLocalStorageKey(parsedEvse.deviceId),
    getDefaultEebusEfficientPvSchedule()
  )

  return {
    ...parsedEvse,

    brand: evse.evseSubTypeParameters?.brand === 'Audi' ? 'Audi' : 'Hager',
    reference: evse.evseSubTypeParameters?.brand === 'Audi' ? '8V4, 9Y0' : 'XEV1S22T2TF',

    protection: evse.parameters?.protection ?? 0,
    holdingTime: evse.parameters?.sunMode?.holdingTimeInMin ?? 0,
    solarRatio: evse.parameters?.minCurrentSunMode ?? 0,
    minCharge: evse.parameters?.minCurrent ?? 0,
    chargingMode,
    safePvSchedule,
    efficientPvSchedule,
    initialPairingId: getEvsePairingId(evse),
    rawData: evse,
  }
}

/**
 * Contains the logic to select the proper charging schedule for an evse
 * @param useServerSchedule if true, the schedule from the server will be used
 * @param serverSchedule the schedule from the server
 * @param localStorageKey key to get the cached schedule from local storage if it exists
 * @param defaultSchedule the fallback schedule to choose if all else are not valid
 */
const getChargingSchedule = (
  useServerSchedule: boolean,
  serverSchedule: ChargingSchedule,
  localStorageKey: string,
  defaultSchedule: ChargingSchedule
): ChargingSchedule => {
  if (useServerSchedule) return serverSchedule

  const cachedSchedule = getLocalStorageValue<ChargingSchedule>(localStorageKey)

  // Reject the schedule if it has no entries.
  if (cachedSchedule && cachedSchedule.length) {
    return cachedSchedule
  }

  return defaultSchedule
}

/** Form Eebus to DTO */
export const getEebusPOSTDto = (eebus: EebusChargingStation): ControlledPOSTDto => {
  return {
    deviceName: sanitizeToLatin(eebus.name),
    type: 'Evse',
    media: 'EEBus',
    parameters: {
      protection: eebus.protection || 0,
      minCurrent: eebus.minCharge || 0,
      phasesManagement: eebus.rawData?.parameters?.phasesManagement ?? '3PhBlocked',
      ledIntensity: eebus.rawData?.parameters?.ledIntensity ?? 100,
      activated: eebus.rawData?.parameters?.activated ?? false,
      authenticationMode: eebus.rawData?.parameters?.authenticationMode ?? 'freeAccess',
      fallbackMaxCurrent: eebus.rawData?.parameters?.fallbackMaxCurrent ?? 0,
      chargeInFallbackModeAllowed: eebus.rawData?.parameters?.chargeInFallbackModeAllowed ?? false,
      minCurrentSunMode: eebus.solarRatio || 0,
      priority: eebus.rawData?.parameters?.priority ?? 100,
      useMidMeter: eebus.rawData?.parameters?.useMidMeter ?? false,
      pulseWeight: eebus.rawData?.parameters?.pulseWeight ?? 0,
      sunMode: getSunMode(eebus.holdingTime || 0, eebus.chargingMode, eebus.safePvSchedule, eebus.efficientPvSchedule),
    },
    evseSubTypeParameters: {
      wallboxId: null,
      brand: eebus.brand,
      wbType: '7',
    },
  }
}

export const getEebusPUTDto = (eebus: EebusChargingStation): ControlledEebusPUTDto => {
  return {
    deviceName: sanitizeToLatin(eebus.name),
    parameters: {
      protection: eebus.protection || 0,
      minCurrent: eebus.minCharge || 0,
      // TODO: check why it's different in PUT than POST
      phasesManagement: eebus.rawData?.parameters?.phasesManagement ?? '1PhBlocked',
      ledIntensity: eebus.rawData?.parameters?.ledIntensity ?? 100,
      activated: eebus.rawData?.parameters?.activated ?? false,
      authenticationMode: eebus.rawData?.parameters?.authenticationMode ?? 'freeAccess',
      fallbackMaxCurrent: eebus.rawData?.parameters?.fallbackMaxCurrent ?? 0,
      minCurrentSunMode: eebus.solarRatio || 0,
      priority: eebus.rawData?.parameters?.priority ?? 100,
      chargeInFallbackModeAllowed: eebus.rawData?.parameters?.chargeInFallbackModeAllowed ?? false,
      lockCable: eebus.rawData?.parameters?.lockCable ?? false,
      useMidMeter: eebus.rawData?.parameters?.useMidMeter ?? false,
      pulseWeight: eebus.rawData?.parameters?.pulseWeight ?? 0,
      sunMode: getSunMode(eebus.holdingTime || 0, eebus.chargingMode, eebus.safePvSchedule, eebus.efficientPvSchedule),
    },
  }
}
